import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";

import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Grid,
} from "@material-ui/core";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import ImageIcon from "@material-ui/icons/Image";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import CloseIcon from "@material-ui/icons/Close";
import FolderIcon from "@material-ui/icons/Folder";

import {
  listTutorialVideos,
  showTutorialVideo,
  buildImageUrl,
} from "../../services/tutorialVideoService";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    backgroundColor: theme.palette.background.default,
  },

  // ── Top bar ─────────────────────────────────────────────────
  topBar: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2.5, 3),
    flexShrink: 0,
  },
  topBarRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5),
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: "1.25rem",
    color: theme.palette.text.primary,
  },
  searchField: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    minWidth: 220,
    "& .MuiOutlinedInput-root": { borderRadius: 8, fontSize: "0.875rem" },
  },
  catsRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  },
  catChip: {
    fontWeight: 500,
    fontSize: "0.8rem",
  },

  // ── Cards area ───────────────────────────────────────────────
  articlesArea: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(3),
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
  },
  cardOuter: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
    overflow: "hidden",
    cursor: "pointer",
    transition: "box-shadow 0.2s, transform 0.15s",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      boxShadow: "0 6px 24px rgba(0,0,0,0.1)",
      transform: "translateY(-2px)",
    },
  },
  cardThumb: {
    width: "100%",
    aspectRatio: "16/9",
    objectFit: "cover",
    backgroundColor: "#111",
    display: "block",
  },
  cardThumbPlaceholder: {
    width: "100%",
    aspectRatio: "16/9",
    backgroundColor: `${theme.palette.primary.main}12`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": { fontSize: 48, color: theme.palette.primary.main, opacity: 0.35 },
  },
  cardBody: {
    padding: theme.spacing(1.5, 2, 2),
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  cardMeta: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: theme.spacing(0.75),
    flexWrap: "wrap",
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    lineHeight: 1.35,
    flex: 1,
  },
  cardDesc: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.55,
    marginTop: theme.spacing(0.5),
    display: "-webkit-box",
    "-webkit-line-clamp": 3,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
  },

  // skeleton
  skeleton: {
    height: 240,
    borderRadius: 14,
    backgroundColor: theme.palette.action.hover,
    animation: "$pulse 1.4s ease-in-out infinite",
  },
  "@keyframes pulse": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.45 },
    "100%": { opacity: 1 },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(8, 2),
    color: theme.palette.text.secondary,
    "& svg": { fontSize: 56, marginBottom: 12, opacity: 0.3 },
  },

  // ── Modal de artigo ─────────────────────────────────────────
  modalPaper: {
    borderRadius: 16,
    maxWidth: 760,
    width: "100%",
    margin: 16,
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "20px 22px 12px",
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  modalTitle: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: theme.palette.text.primary,
    lineHeight: 1.35,
    flex: 1,
    paddingRight: 12,
  },
  modalContent: {
    padding: 0,
    overflowY: "auto",
    maxHeight: "80vh",
    ...theme.scrollbarStyles,
  },
  modalMediaWrapper: {
    width: "100%",
    backgroundColor: "#000",
  },
  modalVideo16x9: {
    position: "relative",
    width: "100%",
    paddingTop: "56.25%",
  },
  modalVideoFrame: {
    position: "absolute",
    top: 0, left: 0,
    width: "100%", height: "100%",
    border: "none",
  },
  modalVideoEl: {
    width: "100%",
    maxHeight: 440,
    display: "block",
    backgroundColor: "#111",
  },
  modalImg: {
    width: "100%",
    maxHeight: 440,
    objectFit: "contain",
    display: "block",
    backgroundColor: "#111",
  },
  modalBody: {
    padding: theme.spacing(2.5, 3, 3),
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
  },
  modalDesc: {
    fontSize: "0.95rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.7,
    whiteSpace: "pre-wrap",
  },
  modalChips: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
}));

const getYouTubeEmbedUrl = (url) => {
  if (!url) return "";
  const m = url.match(/^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
  return m && m[2].length === 11 ? `https://www.youtube.com/embed/${m[2]}?rel=0` : url;
};
const isYoutubeUrl = (url) => url && /youtu\.be|youtube\.com/.test(url);

const CardThumb = ({ video, classes }) => {
  if (video.imageUrl) {
    return <img src={buildImageUrl(video.imageUrl)} alt={video.title} className={classes.cardThumb} />;
  }
  if (video.videoUrl && isYoutubeUrl(video.videoUrl)) {
    const id = (video.videoUrl.match(/(?:v=|youtu\.be\/)([^#&?]*)/) || [])[1];
    if (id) return <img src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`} alt={video.title} className={classes.cardThumb} />;
  }
  return (
    <Box className={classes.cardThumbPlaceholder}>
      <VideoLibraryIcon />
    </Box>
  );
};

const ModalMedia = ({ video, classes }) => {
  if (video.videoUrl && isYoutubeUrl(video.videoUrl)) {
    return (
      <Box className={classes.modalMediaWrapper}>
        <Box className={classes.modalVideo16x9}>
          <iframe
            className={classes.modalVideoFrame}
            src={getYouTubeEmbedUrl(video.videoUrl)}
            title={video.title}
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </Box>
      </Box>
    );
  }
  if (video.videoUrl) {
    const src = video.videoUrl.startsWith("http")
      ? video.videoUrl
      : `${process.env.REACT_APP_BACKEND_URL || ""}${video.videoUrl}`;
    return (
      <Box className={classes.modalMediaWrapper}>
        <video key={src} className={classes.modalVideoEl} controls poster={video.imageUrl ? buildImageUrl(video.imageUrl) : undefined}>
          <source src={src} type="video/mp4" />
          <source src={src} type="video/webm" />
        </video>
      </Box>
    );
  }
  if (video.imageUrl) {
    return (
      <Box className={classes.modalMediaWrapper}>
        <img src={buildImageUrl(video.imageUrl)} alt={video.title} className={classes.modalImg} />
      </Box>
    );
  }
  return null;
};

const Helps = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [tutorialVideos, setTutorialVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [openArticle, setOpenArticle] = useState(null);

  const fetchTutorialVideos = async () => {
    setLoading(true);
    try {
      const { data } = await listTutorialVideos({ searchParam, getAll: true });
      setTutorialVideos(data.tutorialVideos || []);
    } catch {
      toast.error("Erro ao carregar itens de ajuda");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTutorialVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam]);

  const categories = useMemo(() => {
    const cats = new Set(tutorialVideos.map((v) => v.category).filter(Boolean));
    return ["Todos", ...Array.from(cats).sort()];
  }, [tutorialVideos]);

  const countByCategory = useMemo(() => {
    const map = { Todos: tutorialVideos.length };
    tutorialVideos.forEach((v) => {
      if (v.category) map[v.category] = (map[v.category] || 0) + 1;
    });
    return map;
  }, [tutorialVideos]);

  const filtered = useMemo(() => {
    if (selectedCategory === "Todos") return tutorialVideos;
    return tutorialVideos.filter((v) => v.category === selectedCategory);
  }, [tutorialVideos, selectedCategory]);

  const handleCardClick = async (video) => {
    setOpenArticle(video);
    try {
      await showTutorialVideo(video.id, true);
      setTutorialVideos((prev) =>
        prev.map((v) => (v.id === video.id ? { ...v, viewsCount: (v.viewsCount || 0) + 1 } : v))
      );
    } catch {}
  };

  const hasMedia = (v) => !!(v.videoUrl || v.imageUrl);
  const isVideo = (v) => !!v.videoUrl;

  return (
    <Box className={classes.root}>

      {/* ── Top bar ────────────────────────────────────────── */}
      <Box className={classes.topBar}>
        <Box className={classes.topBarRow}>
          <Typography className={classes.pageTitle}>Central de Ajuda</Typography>
          <TextField
            placeholder="Buscar artigo..."
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={(e) => { setSearchParam(e.target.value); setSelectedCategory("Todos"); }}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#bbb", fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Categorias */}
        <Box className={classes.catsRow}>
          {categories.map((cat) => {
            const isActive = selectedCategory === cat;
            return (
              <Chip
                key={cat}
                label={`${cat} (${countByCategory[cat] || 0})`}
                clickable
                size="small"
                icon={<FolderIcon style={{ fontSize: 14 }} />}
                color={isActive ? "primary" : "default"}
                variant={isActive ? "default" : "outlined"}
                onClick={() => setSelectedCategory(cat)}
                className={classes.catChip}
              />
            );
          })}
        </Box>
      </Box>

      {/* ── Grade de artigos ────────────────────────────────── */}
      <Box className={classes.articlesArea}>
        {loading ? (
          <Grid container spacing={3}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box className={classes.skeleton} />
              </Grid>
            ))}
          </Grid>
        ) : filtered.length === 0 ? (
          <Box className={classes.emptyState}>
            <VideoLibraryIcon />
            <Typography variant="body1">Nenhum artigo encontrado</Typography>
            <Typography variant="body2" style={{ marginTop: 4 }}>
              Tente outra categoria ou termo de busca
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filtered.map((video) => (
              <Grid item xs={12} sm={6} md={4} key={video.id}>
                <Box className={classes.cardOuter} onClick={() => handleCardClick(video)}>
                  <CardThumb video={video} classes={classes} />
                  <Box className={classes.cardBody}>
                    <Box className={classes.cardMeta}>
                      {video.category && (
                        <Chip label={video.category} size="small" color="primary" variant="outlined"
                          style={{ fontSize: "0.68rem", height: 20 }} />
                      )}
                      <Chip
                        label={isVideo(video) ? (isYoutubeUrl(video.videoUrl) ? "YouTube" : "Vídeo") : "Imagem"}
                        size="small" variant="outlined"
                        icon={isVideo(video) ? <PlayArrowIcon style={{ fontSize: 12 }} /> : <ImageIcon style={{ fontSize: 12 }} />}
                        style={{ fontSize: "0.68rem", height: 20 }}
                      />
                    </Box>
                    <Typography className={classes.cardTitle}>{video.title}</Typography>
                    {video.description && (
                      <Typography className={classes.cardDesc}>{video.description}</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* ── Modal do artigo ─────────────────────────────────── */}
      <Dialog
        open={!!openArticle}
        onClose={() => setOpenArticle(null)}
        PaperProps={{ className: classes.modalPaper }}
        scroll="paper"
      >
        {openArticle && (
          <>
            <Box className={classes.modalHeader}>
              <Typography className={classes.modalTitle}>{openArticle.title}</Typography>
              <IconButton size="small" onClick={() => setOpenArticle(null)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <DialogContent className={classes.modalContent}>
              <ModalMedia video={openArticle} classes={classes} />
              <Box className={classes.modalBody}>
                <Box className={classes.modalChips}>
                  {openArticle.category && (
                    <Chip label={openArticle.category} size="small" color="primary" variant="outlined"
                      style={{ fontSize: "0.75rem" }} />
                  )}
                  {openArticle.videoUrl && (
                    <Chip
                      label={isYoutubeUrl(openArticle.videoUrl) ? "YouTube" : "Vídeo"}
                      size="small" variant="outlined"
                      icon={<PlayArrowIcon style={{ fontSize: 13 }} />}
                      style={{ fontSize: "0.75rem" }}
                    />
                  )}
                </Box>
                {openArticle.description && (
                  <Typography className={classes.modalDesc}>{openArticle.description}</Typography>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Helps;
